import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ChancenNotification } from '@chancenwerk/db/notifications/ChancenNotification.js';
import '@chancenwerk/design/components/cr-button.js';
import type { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { customElement, property, state } from 'lit/decorators.js';
import { combineLatest, switchMap } from 'rxjs';
/**
 * @customElement chancen-component-notification-card
 */
@customElement('chancen-component-notification-card')
export class ChancenComponentNotificationCard extends ChemistryLitElement {
  @property({ attribute: false }) public notification = new RXController(this, new ChancenNotification({}));
  @state() private readonly _currentUser = new RXController(this, currentUser<ChancenwerkUser>());

  @state() private readonly _seenClientOverride = new RXController(this, false);

  @state() private readonly _seen = new RXController(
    this,
    combineLatest([
      this.notification.observable,
      this._currentUser.observable,
      this._seenClientOverride.observable,
    ]).pipe(
      switchMap(([notification, user, override]) => {
        if (override) {
          return new Promise(resolve => {
            resolve(true);
          });
        }
        return ChancenNotification.getSeenForNotification(user?._id ?? '', notification._id)();
      }),
    ),
  );

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          borderLeft: `1px solid ${this._seen.value === true ? this.colors.primary : this.colors.secondary}`,
          background: `${this._seen.value === true ? this.colors.primary.shade(50) : this.colors.secondary.shade(50)}`,
          borderRadius: `0 ${this.sizes.borderRadius} ${this.sizes.borderRadius} 0`,
          width: '100%',
        },
      },
    ];
  }

  override render() {
    if (this._seen.isLoading || this._currentUser.isLoading) return html` <x-flat-loader flex></x-flat-loader> `;
    return html`
      <cr-flex
        w100
        space="xs"
        ${css({ paddingInline: this.spacing.sm, paddingTop: this.spacing.sm, paddingBottom: this.spacing.xs })}
      >
        <cr-flex horizontal space-between crossaxis-center>
          <cr-h4>${this.notification.value.title}</cr-h4>
          <cr-text ${css({ opacity: '0.75' })}>${this.notification.value.createdAt?.toFormat('dd LLL yyyy')}</cr-text>
        </cr-flex>

        <cr-text>${unsafeHTML(this.notification.value.content)}</cr-text>
        <cr-flex horizontal space-between crossaxis-center>
          <cr-text ${css({ opacity: '0.75' })}>Von: ${this.notification.value.creatorName}</cr-text>
          ${!this._seen.value
            ? html`
                <cr-button
                  mode="icon"
                  icon="visibility"
                  ${css({ color: this.colors.secondary })}
                  @click=${async () => {
                    this._seenClientOverride.next(true);
                    await ChancenNotification.setSeenForNotification(
                      this._currentUser.value?._id ?? '',
                      this.notification.value._id,
                    )();
                  }}
                >
                  <cr-tooltip>
                    <cr-text> Als "gesehen" markieren </cr-text>
                  </cr-tooltip>
                </cr-button>
              `
            : nothing}
        </cr-flex>
      </cr-flex>
    `;
  }
}
